import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useQueryLinkNavigate from '../../../hooks/useQueryLinkNavigate';
import useQueryString from '../../../hooks/useQueryString';
import Main from '../../../component/Main';
import Loading from '../../../component/common/loading/Loading';
import MergedTableHeader from '../../../component/common/dataGrid/MergedTableHeader';
import OrgParticipate from './OrgParticipate';
import Pagination from '../../../component/common/dataGrid/Pagination';
import ExcelDownload from '../../../component/common/button/ExcelDownload';

import { useSubIcon } from '../../../hooks/useSubIcon';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { Search16w } from '../../../images/icons/icons_component';

const OrgDetail = () => {

	const { returnPage } = useQueryLinkNavigate();

	// 쿼리스트링 불러오기
	const { orgSearchObj, pageNo, itemsPerPage, setOrgParams, searchParamsStr } = useQueryString();

	// 입력값 임시 저장 상태
	const [ tempSearch, setTempSearch ] = useState(orgSearchObj);

	const [locaList, setLocaList] = useState([]);
	const [dstrList, setDstrList] = useState([]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setTempSearch(prev => ({
            ...prev,
            [name]: value
        }));
	};

	const dataHead = ['번호', '응급의료 기관분류', '기관분류', '기관명', '지역', '운영상태', '대표전화', '주소', '수정'];
	const dataColumn = ['rnum', 'grdcname', 'emdvname', 'emogdesc', 'locaname', 'lvklname', 'emogtel1', 'addrname'];

	const [participation, setParticipation] = useState({
		emogcode: '',
		orgOpen: false
	})

	const [state, setState] = useState({
		dataList: [],
		isSearched: false,
		isLoading: false
	})

    // 기관 목록 조회
    const fetchOrg = async (newParams, newPage, newPerPage) => {
        
        const totalObj = Object.assign({}, {pageNum: newPage, displayRowsize: newPerPage}, newParams);

        setState(prev => ({
            ...prev,
            isSearched: false,
            isLoading : true
        }));

        try {
            const response = await axios.get('/commonapi/agent/selectOrgList.do',
                { params: totalObj }
            );
			const responseCnt = await axios.get('/commonapi/agent/selectOrgListCount.do',
                { params: totalObj }
            );
            setTotalItems(responseCnt.data);
            setState(prev => ({
                ...prev,
                dataList: response.data,
                isSearched: true,
                isLoading : false
            }));
            setTempSearch(newParams);
        } catch(e) {
            console.error('body 내용 로딩 중 오류 발생', e);
        }
    }

    // 엔터 이벤트 추가
    const activeEnter = (e) => {
        if(e.key === 'Enter') {
            setOrgParams({...tempSearch, page: 1, itemsPerPage: itemsPerPage || 10});
        }
    }

    const onSearch = () => {
        setOrgParams({...tempSearch, page: 1, itemsPerPage: itemsPerPage || 10});
    };
    
	useEffect(() => {
		fetchOrg(orgSearchObj, Number(pageNo), Number(itemsPerPage));
	}, [searchParamsStr]);

	useEffect(() => {
		const fetchLoca = async () => {
			try {
				const response = await axios.get('/commonapi/agent/selectCodeList.do', {
					params: {
						bscdmain: "EMIMEMOG",
						bscdmidl: "LOCA"
					}
				});
				setLocaList(response.data);
			}
			catch (error) {
				console.error('초기 데이터 로딩 중 오류 발생:', error);
			}
		};
		fetchLoca();
	}, []);

	useEffect(() => {
		const fetchGugun = async () => {
			try {
				const response = await axios.get('/commonapi/agent/selectGugunListBySido.do', {
					params: tempSearch
				});
				setDstrList(response.data.gugunList);
			}
			catch (error) {
				console.error('초기 데이터 로딩 중 오류 발생:', error);
			}
		}
		fetchGugun();
	}, [tempSearch.searchLoca]);

    // 페이지네이션 handler
	const onHandlePage = (page) => {
		setOrgParams({ ...orgSearchObj, page: page, itemsPerPage: itemsPerPage });
	};

	const [totalItems, setTotalItems] = useState(0);

	const paginationInfo = {
		totalItems,
        currentPage: pageNo || 1,
		itemsPerPage: itemsPerPage || 10,
		onPageChange: onHandlePage,
	}

    const perPageOptions = Array.from({ length: 10 }, (_, i) => {
        const perPage = (i + 1) * 10;
        return (
            <option key={perPage} value={perPage}>
                -- {perPage}건 씩 조회 --
            </option>
        );
    });

	const computeRowSpans = (dataList, mergeConfig = [], allColumns = []) => {
		const rowSpans = dataList?.map(() => ({}));
		// 병합 기준이 되는 마스터 컬럼
		const masterMerge = mergeConfig.find(m => m.row === null);
		const masterKey = masterMerge?.key;
		// 1. 기준 컬럼 기준으로 병합 구간 나누기
		const mergeBlocks = [];
		let blockStart = 0;
		for (let i = 1; i <= dataList?.length; i++) {
			const curr = dataList[i];
			const prev = dataList[i - 1];
			const isSameGroup =
				i < dataList?.length &&
				dataList[i][masterKey] === dataList[i - 1][masterKey] &&
				dataList[i][masterKey] !== null &&
				dataList[i][masterKey] !== '';
			if (!isSameGroup) {
				mergeBlocks.push({ start: blockStart, end: i });
				blockStart = i;
			}
		}
		// 2. 기준 컬럼 병합 수행
		mergeBlocks.forEach(({ start, end }) => {
			const masterValue = dataList[start][masterKey];
			rowSpans[start][masterKey] = end - start;
			for (let i = start + 1; i < end; i++) {
				rowSpans[i][masterKey] = 0;
			}
			// 3. 그 외 병합 대상 칼럼 처리
			mergeConfig.forEach(({ key: colKey, row, nullable }) => {
				if (colKey === masterKey) return;
				let prevValue = null;
				let localStart = start;
				let mergeCount = 1;
				for (let i = start; i < end; i++) {
					const currVal = dataList[i][colKey];
					const isMergeable = nullable
						? currVal === prevValue
						: currVal === prevValue && currVal !== '' && currVal !== null;
					if (i === start) {
						rowSpans[i][colKey] = 1;
						prevValue = currVal;
						continue;
					}
					if (isMergeable) {
						rowSpans[localStart][colKey] += 1;
						rowSpans[i][colKey] = 0;
						mergeCount += 1;
					} else {
						rowSpans[i][colKey] = 1;
						localStart = i;
						mergeCount = 1;
						prevValue = currVal;
					}
				}
			});
		});
		// 4. 병합 대상이 아닌 컬럼 처리
		dataList?.forEach((_, i) => {
			allColumns.forEach(colKey => {
				if (!(colKey in rowSpans[i])) {
					rowSpans[i][colKey] = 1;
				}
			});
		});
		return rowSpans;
	};

	const rowSpans = useMemo(() => {
		return computeRowSpans(state.dataList, [], dataColumn);
	}, [state.dataList]);

	// Dynamic Style Hook
	const { lightBackColor, borderColorGray, hoverFontColor, primaryBackColor } = useDynamicStyle();

	// icon list
	const { EmptyIcon, NullIcon } = useSubIcon();

	return (
		<Main>
			{participation.orgOpen ? <OrgParticipate emogcode={participation.emogcode} /> :
			<>
				<div className="com-search">
					<table>
						<colgroup>
							<col width="6%" />
							<col width="13%" />
							<col width="8%" />
							<col width="18%" />
							<col width="5%" />
							<col width="12%" />
							<col width="6%" />
							<col width="12%" />
						</colgroup>
						<tbody>
							<tr>
								<th>센터구분</th>
								<td>
									<select
										id="searchEmog"
										name="searchEmog"
										className='w-full'
										value={tempSearch?.searchEmog || ''}
										onChange={handleInputChange}
									>
										<option value="">전체</option>
										<option value="A">권역응급의료센터</option>
										<option value="C">지역응급의료센터</option>
										<option value="D">지역응급의료기관</option>
									</select>
								</td>
								<th>지역 (시/도)</th>
								<td>
									<select
										id="searchLoca"
										name="searchLoca"
										value={tempSearch?.searchLoca || ''}
										onChange={handleInputChange}
									>
										<option value="">시/도</option>
										{locaList?.map(loca => (
											<option key={loca.bscdminr} value={loca.bscdminr}>
												{loca.bscddesc}
											</option>
										))}
									</select>
									&nbsp;
									<select
										id="searchType"
										name="searchType"
										value={tempSearch?.searchType || ''}
										onChange={handleInputChange}
									>
										<option value="">구/군</option>
										{dstrList?.map(dstr => (
											<option key={dstr.ditCod} value={dstr.ditCod}>
												{dstr.ditNam}
											</option>
										))}
									</select>
								</td>
								<th>기관명</th>
								<td>
									<input
										type="text"
										id="searchCheck1"
										className='input_style'
										name="searchCheck1"
										value={tempSearch?.searchCheck1 || ''}
										onChange={handleInputChange}
										onKeyDown={(e) => activeEnter(e)}
									/>
								</td>
								<th>기관코드</th>
								<td>
									<input
										type="text"
										id="searchCheck2"
										className='input_style'
										name="searchCheck2"
										value={tempSearch?.searchCheck2 || ''}
										onChange={handleInputChange}
										onKeyDown={(e) => activeEnter(e)}
									/>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div className='search_btn_wrapper'>
					<span className='total_items'>전체&nbsp;<strong className='ho_primary'>{totalItems}</strong>건</span>
					<div className='btn_container'>
						<select
							name="itemsPerPage"
							className='page_select'
							value={itemsPerPage}
							onChange={(e) => {
								setOrgParams({
									...tempSearch,
									itemsPerPage: parseInt(e.target.value),
									page: 1
								});
							}}
						>
							{perPageOptions}
						</select>
						<button onClick={() => onSearch()} disabled={state.isLoading ? 'disabled' : ''} className={`search_btn bg_ho_primary`}><Search16w />검색</button>
						<ExcelDownload
							excelHead={dataHead}
							excelData={state.dataList}
							searched={state.isSearched}
							dataCode={dataColumn}
							excelFileName='기관관리 리스트'
						/>
					</div>
				</div>
				<div className='data_grid_container'>
					{state?.isLoading ? <Loading /> :
						<div className='flex flex-col w-full h-full overflow-hidden'>
							<div className='data_responsive'>
								<hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
								<div className={`data_list_table ${state.isSearched ? 'grid-animation' : ''}`}>
									<table>
										<MergedTableHeader
											headers={dataHead}
										/>
										<tbody>
											{state.dataList?.length === 0 ? (
												<tr>
													<td colSpan={1000} className='empty_box'>
														<span className={`data_empty ${lightBackColor}`}>
															{state.isSearched ? EmptyIcon : NullIcon}
															{state.isSearched ? '해당하는 데이터가 없습니다.' : '검색 버튼을 눌러 데이터를 조회하세요.'}
														</span>
													</td>
												</tr>
											) : (
												state.dataList?.map((item, rowIndex) => {
													const key = rowIndex;
													const select = key;
													const gridColor = rowIndex % 2 === 0 ? lightBackColor : '';
													const goDetail = () => {
														returnPage('org', state.dataList[rowIndex].emogcode, searchParamsStr)
													};
													return (
														<tr key={key} className={`${gridColor} ${hoverFontColor}`}>
															{dataColumn.map((col, colIndex) => {
																const span = rowSpans[rowIndex][col];
																const style = undefined;
																if (span) {
																	return (
																		<td key={colIndex} rowSpan={span} style={style}>
																			{item[col]}
																		</td>
																	);
																} else {
																	return null;
																}
															})}
															<td>
																<button onClick={goDetail} disabled={state.isLoading ? 'disabled' : ''} className={`search_btn m-auto ${primaryBackColor}`}>수정하기</button>
															</td>
														</tr>
													)
												})
											)}
										</tbody>
									</table>
								</div>
								<hr className='mr-1-1 co_border_gray' />
							</div>
							{state.dataList?.length === 0  ? (
								<br />
							) : (
								<Pagination
									paginationInfo={paginationInfo}
								/>
							)}
						</div>
					}
				</div >
			</>
			}
		</Main>
	)
}

export default OrgDetail;